/**
 * FontPicker — shared dropdown + preview thumbnail for selecting a font
 * from the admin font library. Used in:
 *  - Content pack editor (default_font_id)
 *  - Workflow lucky_charm node options (font_id)
 *  - Playground lucky charm testing panel
 */

import { Link } from 'react-router-dom'
import { useFontsList } from '@/features/fonts/hooks/useFonts'
import { FontPreview } from './FontPreview'
import type { FontPickerProps } from './types'

export function FontPicker({
  value,
  onChange,
  fallbackLabel = '使用全局默认',
  showManageLink = true,
  compact = false,
  previewText = '幸运',
  disabled = false,
}: FontPickerProps) {
  const { data: fonts = [], isLoading } = useFontsList(false)

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {value !== null ? (
          <FontPreview fontId={value} text={previewText} alt="font preview" className="h-8 rounded" />
        ) : (
          <span className="text-xs text-gray-500">{fallbackLabel}</span>
        )}
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          disabled={disabled || isLoading}
          className="text-xs px-2 py-1 border rounded"
        >
          <option value="">{fallbackLabel}</option>
          {fonts.map((f) => (
            <option key={f.id} value={f.id}>
              {f.display_name}
              {f.is_global_default ? ' (默认)' : ''}
            </option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <div className="flex items-stretch gap-2">
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          disabled={disabled || isLoading}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
        >
          <option value="">{fallbackLabel}</option>
          {fonts.map((f) => (
            <option key={f.id} value={f.id}>
              {f.display_name}
              {f.is_global_default ? ' (默认)' : ''}
            </option>
          ))}
        </select>
        {value !== null && (
          <FontPreview
            fontId={value}
            text={previewText}
            alt="preview"
            className="h-10 w-24 object-contain border border-gray-200 rounded bg-white"
          />
        )}
      </div>
      {showManageLink && (
        <Link
          to="/content-packs#fonts-section"
          className="text-xs text-blue-600 hover:underline"
        >
          管理字体 →
        </Link>
      )}
    </div>
  )
}
